import {
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    useToast,
    useDisclosure,
} from '@chakra-ui/react';
import { useDeleteClientProductV2Mutation } from 'core/API/productsApi';
import { useTranslation } from 'helpers/languages/translations';
import { IProductTitle, TRetailerProp } from 'shared/types';
import { isFetchBaseQueryError } from 'utils/erros';

type TDeleteProductModalProps = {
    productToUpdate: IProductTitle;
    retailer: TRetailerProp;
    lang: string;
    onDeleteClose: () => void;
    isDeleteOpen: boolean;
};

const DeleteProductModal = ({
    onDeleteClose,
    isDeleteOpen,
    productToUpdate,
    retailer,
    lang,
}: TDeleteProductModalProps) => {
    const translate = useTranslation();
    const toast = useToast();

    const [deleteProducts, { isLoading: isDeleteLoading }] = useDeleteClientProductV2Mutation();

    const handleDelete = async () => {
        try {
            const res = await deleteProducts({
                products: [productToUpdate.ean],
                retailer: retailer === 'default' ? 'default' : `${retailer.id}`,
                lang,
                isProductTitle: true,
            });

            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as { message: string }).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error(translate('product.delete.error') as string);
            }

            onDeleteClose();
            const nb = res.data.deleted;
            toast({
                title: nb > 0 ? translate('product.delete.success') : translate('splits.delete.inherited'),
                status: nb > 0 ? 'success' : 'info',
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: (err as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    return (
        <Modal isCentered isOpen={isDeleteOpen} onClose={onDeleteClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text as="h3">{translate('product.delete.header')}</Text>
                </ModalHeader>
                <ModalCloseButton mt="2" isDisabled={isDeleteLoading} />
                <ModalBody fontSize="sm">{translate('splits.delete.confirmation')}</ModalBody>

                <ModalFooter>
                    <Button isDisabled={isDeleteLoading} variant="outline" size="sm" mr={3} onClick={onDeleteClose}>
                        {translate('product.delete.cancel')}
                    </Button>
                    <Button onClick={handleDelete} isLoading={isDeleteLoading} colorScheme="red" size="sm">
                        {translate('product.delete.apply')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default DeleteProductModal;
